import {
  AttachmentFileBase,
  Conversation,
  CustomGroup,
  ConversationsResponse as Response,
  convertConversation,
} from '@nextforce/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useServer } from 'app/server/context'
import { appendFileToFormData } from 'chat/shared/util'
import { useChatWebsocketContext } from 'chat/websocket/context'
import { addConversationToCacheIfNeeded } from './shared/utils'

export function useCreateCustomConversation() {
  const { server } = useServer()
  const client = useQueryClient()
  const { sendWebsocketMessage } = useChatWebsocketContext()

  return useMutation({
    mutationFn: createCustomConversation,
    onSuccess: async customConversation => {
      await addConversationToCacheIfNeeded(client, customConversation)
      sendWebsocketMessage({
        action: 'conversationEvent',
        conversationEvent: customConversation,
      })
    },
  })

  async function createCustomConversation({
    group,
    companyId,
  }: {
    group: CustomGroup
    companyId?: string
  }): Promise<Conversation> {
    const formData = await getCustomForm()
    const responseConversation = await server.post<Response.Conversation>(
      `/v2/chat/conversations/`,
      formData,
      'user',
      'multipart/form-data'
    )

    return convertConversation(responseConversation)

    async function getCustomForm() {
      const form = new FormData()
      form.append('name', group.name)
      form.append('manager_read_only', group.managerReadOnly ? 'true' : 'false')
      form.append('crew_read_only', group.crewReadOnly ? 'true' : 'false')

      if (group.description) {
        form.append('description', group.description)
      }

      if (group.icon) {
        const file: AttachmentFileBase = {
          name: 'icon',
          uri: group.icon.uri,
          mimeType: group.icon.mimeType,
        }
        const formFieldName = 'icon'
        await appendFileToFormData(form, formFieldName, file)
      }

      if (group.repository) {
        form.append('repository', group.repository ? 'true' : 'false')
      }

      if (companyId) {
        form.append('company_id', companyId)
      }

      return form
    }
  }
}
